// import styled from 'styled-components';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { SummaryContext } from '@/features/details/summary/context';
import { ExperienceVariant } from '@/webapi/use-experience-api';
import { getStatisticalTestResult } from '@/features/details/summary/util';
import { SummaryHeader } from '@/features/details/summary/summary-header';
import { Tooltip } from '@/components/tooltip';
import { AbTestResult } from '@/webapi/use-analytics-api';
import { breakpoints } from '@/components/responsive';

const scaleWidth = 140;

interface ConfidenceIntervalsProps {
  variant?: ExperienceVariant;
  uplift: number;
}

const formatPercentile = (v: number) => {
  const number = v * 100;
  if (number > 100) {
    return number.toFixed(0);
  }
  return number.toFixed(2);
};

const calcIntervalWidth = (low: number, high: number) =>
  Math.ceil((Math.min(Math.abs(low), 1) * scaleWidth) / 2) +
  Math.ceil((Math.min(Math.abs(high), 1) * scaleWidth) / 2);

const calcIntervalColor = (high: number, low: number) => {
  const green = `#47DF73`;
  const orange = `#DA5412`;
  let color = `#a6afb8`;
  if (high > 0 && low > 0) {
    color = green;
  } else if (high < 0 && low < 0) {
    color = orange;
  } else if (high > 0 && low < 0) {
    if (high / (high + Math.abs(low)) > 0.7) {
      color = green;
    } else if (Math.abs(low) / (high + Math.abs(low)) > 0.7) {
      color = orange;
    }
  }
  return color;
};

const calcIntervalOffset = (low: number, width: number) => {
  let offset = scaleWidth / 2 - Math.min(Math.abs(low), 1) * (scaleWidth / 2);
  if (width === scaleWidth) {
    offset = 0;
  }
  return offset;
};

const calcIntevalLowHigh = (
  binomial: AbTestResult,
  gaussian: AbTestResult,
  uplift: number,
) => {
  const expected = binomial?.expected || gaussian?.expected;
  let low = binomial?.ci?.[0] || gaussian?.ci?.[0];
  let high = binomial?.ci?.[1] || gaussian?.ci?.[1];

  low = (low / Math.abs(expected)) * (Math.abs(uplift) / 100);
  high = (high / Math.abs(expected)) * (Math.abs(uplift) / 100);
  let scalingFactor = 1;
  if (Math.abs(low) + Math.abs(high) < 0.4) {
    scalingFactor = 2;
    low *= scalingFactor;
    high *= scalingFactor;
  }

  return { low, high, scalingFactor };
};

const hasInsufficientInfoForCI = (
  high: number,
  scalingFactor: number,
  low: number,
) => {
  if (high === 0 && low === 0) {
    return true;
  }
  if (!Number.isFinite(high) || !Number.isFinite(low)) {
    return true;
  }

  return (high / scalingFactor) * 100 > 99 || (low / scalingFactor) * 100 < -99;
};

export function ConfidenceIntervals({
  variant,
  uplift,
}: ConfidenceIntervalsProps) {
  const { data } = useContext(SummaryContext);
  const { binomial, gaussian } = getStatisticalTestResult(data, variant);
  const { low, high, scalingFactor } = calcIntevalLowHigh(
    binomial,
    gaussian,
    uplift,
  );
  let width = calcIntervalWidth(low, high);
  const color = calcIntervalColor(high, low);
  const offset = calcIntervalOffset(low, width);
  if (offset + width > scaleWidth) {
    width -= offset;
  }
  if (variant.id === data?.controlVariationId) {
    return <span style={{ marginTop: `0.5rem`, color: `#A6AFB8` }}>---</span>;
  }
  if (hasInsufficientInfoForCI(high, scalingFactor, low)) {
    return (
      <span
        style={{
          marginTop: `0.5rem`,
          color: `#A6AFB8`,
        }}
      >
        Insufficient <br />
        data for CI
      </span>
    );
  }
  const allPositive = low > 0 && high > 0;
  const allNegative = low < 0 && high < 0;
  return (
    <Wrapper className="ci-graph">
      <Container scaleWidth={scaleWidth}>
        <Center>
          <p>0%</p>
          <p>|</p>
        </Center>
        <Interval offset={offset} color={color} width={width}>
          <Low>{formatPercentile(low / scalingFactor)}%</Low>
          <High>
            <span style={{ marginLeft: `1rem` }}>{high > 0 ? `+` : ``}</span>
            {formatPercentile(high / scalingFactor)}%
          </High>
        </Interval>
        {allNegative && (
          <div
            style={{
              position: `absolute`,
              right: 0,
              top: 0,
              bottom: 0,
              width: `${scaleWidth / 2}px`,
              background: `#F2F2F2`,
              opacity: 1,
              borderRadius: `0 15px 15px 0`,
            }}
          />
        )}
        {allPositive && (
          <div
            style={{
              position: `absolute`,
              left: 0,
              top: 0,
              bottom: 0,
              width: `${scaleWidth / 2}px`,
              background: `#F2F2F2`,
              opacity: 1,
              borderRadius: `15px 0 0 15px`,
            }}
          />
        )}
      </Container>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  margin-top: 0.5rem;
  width: 0;
  ${breakpoints.down(`md`)} {
    margin-bottom: 0.5rem;
    margin-left: 0.5rem;
  }
`;
const Low = styled.span`
  margin-top: 34px;
  font-family: Inter, serif;
  font-size: 12px;
  font-weight: 600;
`;

const High = styled.span`
  font-family: Inter, serif;
  margin-top: 34px;
  font-size: 12px;
  font-weight: 600;
`;

const Center = styled.span`
  p {
    font-family: Inter, serif;
    margin: 0;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  font-size: 1.2rem;
  color: #a6afb8 !important;
  margin-bottom: 20px;
`;

const Interval = styled.div<{ number: number; width: number; color: string }>`
  position: absolute;
  background-color: ${(p) => p.color};
  height: 1rem;
  left: ${(p) => p.offset}px;
  width: ${(p) => p.width}px;
  border-radius: 15px;
  display: flex;
  justify-content: ${(p) => (p.width < 74 ? `center` : `space-between`)};
  align-items: center;
  span {
    color: ${(p) => p.color} !important;
  }
`;

const Container = styled.div`
  left: -50px;
  position: relative;
  width: ${(p) => p.scaleWidth}px;
  height: 1rem;
  align-self: center;
  border-radius: 15px;
  background-color: #f2f2f2;
  display: flex;
  justify-content: center;
  align-items: center;

  ${breakpoints.down(`md`)} {
    left: unset;
    scale: 0.85;
  }
`;

export function CIToolTip() {
  return (
    <Tooltip
      zIndex={0}
      title="Confidence Interval"
      text={
        <span>
          The confidence interval shows the possible range in which the true
          result is likely to fall. For example, if a variation has a conversion
          rate of 5% with a confidence interval of 4%–6%, it means the true
          conversion rate is probably somewhere within that range. This helps
          you understand how confident the system is in the results.
        </span>
      }
    />
  );
}

export function CIHedaer() {
  return (
    <SummaryHeader>
      CI
      <CIToolTip />
    </SummaryHeader>
  );
}
